import { Controller } from "@hotwired/stimulus"
import BryntumImporter from "../lib/bryntum_importer"
import { collectBaselines, baselineFor } from "../lib/schedule_baseline_carry"
import { track } from "../lib/mixpanel"

// The PDF paper list shows each size's measurements (Phelipe 2026-09-24), e.g.
// "A3 (11.7 × 16.5 in)". Keys are Bryntum's PaperFormat names plus our Tabloid
// and Ledger (the same 11×17 sheet, upright and sideways).
const PAPER_SIZES = {
  A0: "33.1 × 46.8 in", A1: "23.4 × 33.1 in", A2: "16.5 × 23.4 in", A3: "11.7 × 16.5 in", A4: "8.3 × 11.7 in",
  A5: "5.8 × 8.3 in", A6: "4.1 × 5.8 in", Legal: "8.5 × 14 in", Letter: "8.5 × 11 in",
  Tabloid: "11 × 17 in", Ledger: "17 × 11 in"
}

export default class extends Controller {
  static values = {
    scheduleId: Number,
    projectId: Number,
    loadUrl: String,
    syncUrl: String,
    importUrl: String,
    saveSettingsUrl: String,
    quickUpdateUrl: String,
    healthUrl: String,
    ganttSettings: { type: Object, default: {} },
    startDate: String,
    isReadOnly: { type: Boolean, default: false },
    locale: { type: String, default: "en" },
    csrfToken: String,
    i18nNewActivity: { type: String, default: "New Activity" },
    i18nToday: { type: String, default: "Today" },
    i18nCalendarUpdated: { type: String, default: "Working time calendar updated successfully" },
    i18nSelectTaskFirst: { type: String, default: "Please select a task first." },
    i18nImporting: { type: String, default: "Importing project..." },
    i18nImportSuccess: { type: String, default: "Schedule imported successfully" },
    i18nImportFailed: { type: String, default: "Import failed" },
    i18nStartDateUpdated: { type: String, default: "Start date updated" },
    i18nStartDateFailed: { type: String, default: "Failed to update start date" },
    i18nSyncFailed: { type: String, default: "Failed to save changes. Please try again." },
    i18nLoopLinkOff: { type: String, default: "Some activities are linked in a loop (%{links}). That link is ignored until you fix it." },
    i18nAddToCalendar: { type: String, default: "Add to Calendar (.ics)" },
    i18nOnlyMppXer: { type: String, default: "Only .mpp, .xml and .xer files are supported." },
    i18nActivity: { type: String, default: "Activity" },
    i18nResources: { type: String, default: "Resources" },
    i18nResourcesSaved: { type: String, default: "Resources updated" },
    // K2-3 (Larry 2026-09-24): the company's Crew Schedule trades, offered in the
    // Resources picker, and where a trade typed there is saved (blank = no Crew Schedule).
    crewTrades: { type: Array, default: [] },
    crewTradesUrl: String,
    i18nAddTrade: { type: String, default: "Add a trade" },
    i18nAdd: { type: String, default: "Add" },
    i18nTradeAdded: { type: String, default: "%{name} added" },
    i18nTradeNotSaved: { type: String, default: "%{name} was added here, but not to your Crew Schedule's trades" },
    i18nQuantity: { type: String, default: "Quantity" },
    i18nPctDone: { type: String, default: "% Done" },
    // The PDF's page header (K2-2, Larry 2026-09-24): which project this is, and when it was printed.
    projectName: String,
    projectNumber: String,
    projectAddress: String,
    i18nPdfPrinted: { type: String, default: "Printed %{date}" },
    i18nPdfPage: { type: String, default: "Page %{page} of %{total}" },
    deleteUrl: String
  }

  static targets = [
    "container", "importFile", "importModal", "startedAtInput", "startedAtModal",
    "scheduleSelector", "startDateDisplay", "deleteModal",
    "criticalPathBtn", "baselineBtn"
  ]

  connect() {
    this._syncGanttThemeStylesheets()
    // Follow the app's light/dark switch live (profile menu, or the OS when
    // the app is on "system"), no reload needed.
    this._themeObserver = new MutationObserver(() => this._syncGanttThemeStylesheets())
    this._themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] })
    this._showPendingFlash()
    track("Schedule Viewed")
    // Store bound reference so removeEventListener works correctly
    this._boundOnHealthUpdate = this._onHealthUpdate.bind(this)
    // The Resources pop-up (schedule_resources_controller) asks for the list
    // and hands back the changes; the stores do the saving (autoSync).
    this._onResourcesRequest = () => this._provideResources()
    this._onResourcesApply = (event) => this._applyResources(event.detail || {})
    window.addEventListener("schedule-resources:request", this._onResourcesRequest)
    window.addEventListener("schedule-resources:apply", this._onResourcesApply)
    if (window.bryntum?.gantt) {
      this._boot()
    } else {
      // Turbo navigation: Stimulus connects before the <script> in <head> finishes loading.
      this._waitForBryntum()
    }
  }

  // Every way into the Schedule is a Turbo visit now, not a full reload (K3-8:
  // a reload ended screen recordings), so the first visit in a tab waits for
  // the library to download — about 13 MB, more than the old 5-second wait
  // allowed on a slow line. Wait up to two minutes; stop if the script fails.
  _waitForBryntum(attempts = 0) {
    if (window.bryntum?.gantt) { this._boot(); return }
    if (attempts === 0) {
      document.querySelector('script[src$="/vendor/bryntum/gantt.umd.js"]')
        ?.addEventListener("error", () => { this._bryntumFailed = true }, { once: true })
    }
    if (this._bryntumFailed || attempts > 1200) { console.warn("Bryntum Gantt library failed to load"); return }
    this._bryntumTimer = setTimeout(() => this._waitForBryntum(attempts + 1), 100)
  }

  async _boot() {
    await this._initLocale()
    this._addTabloid()
    this._initProject()
    this._initGantt()
    this._attachListeners()
    document.addEventListener("schedule:task-updated-from-health", this._boundOnHealthUpdate)
  }

  disconnect() {
    this._stopSaveWatch()
    document.removeEventListener("schedule:task-updated-from-health", this._boundOnHealthUpdate)
    clearTimeout(this._bryntumTimer)
    clearTimeout(this._settingsSaveTimer)
    window.removeEventListener("schedule-resources:request", this._onResourcesRequest)
    window.removeEventListener("schedule-resources:apply", this._onResourcesApply)
    this._themeObserver?.disconnect()
    // Don't leave the schedule's theme flag on <html> after navigating away.
    document.documentElement.removeAttribute("data-gantt-theme")
    if (this.gantt) {
      this.gantt.destroy()
      this.gantt = null
    }
  }

  // ─── Resources (Phelipe 2026-09-22) ─────────────────────────────────
  // The Resources column always could assign from the resource store; the
  // pop-up is how names get INTO it. Removing a resource takes its
  // assignments with it (Bryntum drops them), which the pop-up warns about.

  // Toolbar button (it sits inside this element, so the action lands here).
  openResources() {
    this._provideResources()
  }

  _provideResources() {
    const project = this.gantt?.project
    if (!project) return
    const counts = {}
    project.assignmentStore.forEach((a) => { counts[a.resourceId] = (counts[a.resourceId] || 0) + 1 })
    const resources = project.resourceStore.records.map((r) => ({ id: r.id, name: r.name, assigned: counts[r.id] || 0 }))
    window.dispatchEvent(new CustomEvent("schedule-resources:provide", { detail: { resources } }))
  }

  _applyResources({ added = [], renamed = [], removed = [] }) {
    const project = this.gantt?.project
    if (!project) return
    const store = project.resourceStore
    removed.forEach((id) => {
      const record = store.getById(id)
      if (record) store.remove(record)
    })
    renamed.forEach(({ id, name }) => {
      const record = store.getById(id)
      if (record) record.name = name
    })
    if (added.length) store.add(added.map((name) => ({ name })))
    this._showToast(this.i18nResourcesSavedValue)
  }

  // ─── Crew Schedule trades in the Resources picker (K2-3, Larry 2026-09-24) ──
  // While the picker is open the company's Crew Schedule trades join the
  // schedule's resources in it; the ones picked for the task are saved with it
  // and the rest leave again. Saving waits until the edit ends and offering or
  // withdrawing a trade stays out of Undo, so a trade nobody picked is never
  // saved. The box beside Save adds a name to the list and, for a company on
  // the Crew Schedule, to its trades too.

  _tradeName(name) { return String(name || "").replace(/\s+/g, " ").trim().slice(0, 50) }

  _tradeKey(name) { return this._tradeName(name).toLowerCase() }

  _offerTrades({ editorContext } = {}) {
    const { column, record } = editorContext || {}
    const project = this.gantt?.project
    if (column?.type !== "resourceassignment" || !record || !project) return
    this._settleTrades()
    const field = column.editor
    if (!field?.store) return
    const store = project.resourceStore
    const have = new Set(store.records.map((r) => this._tradeKey(r.name)))
    const names = []
    this.crewTradesValue.forEach((trade) => {
      const name = this._tradeName(trade), key = name.toLowerCase()
      if (name && !have.has(key)) { have.add(key); names.push(name) }
    })
    if (names.length) {
      project.suspendAutoSync()
      // A save already queued would carry the offered trades along: it waits for the edit's end instead.
      project.clearTimeout("autoSync")
      const records = this._withoutUndo(project, () => store.add(names.map((name) => ({ name }))))
      this._tradeOffer = { project, records }
      // The column filled its picker before the trades were in: fill it again.
      field.store.projectEvent = null
      field.projectEvent = record.$original || record
    }
    this._addTradeBox(field)
  }

  _settleTrades() {
    const offer = this._tradeOffer
    if (!offer) return
    this._tradeOffer = null
    const { project, records } = offer
    const used = new Set(project.assignmentStore.records.map((a) => a.resource))
    const unused = records.filter((r) => !used.has(r) && project.resourceStore.includes(r))
    if (unused.length) this._withoutUndo(project, () => project.resourceStore.remove(unused))
    project.resumeAutoSync()
    const picked = records.filter((r) => used.has(r)).length
    if (picked) track("Schedule Trades Assigned", { count: picked, project_id: this.projectIdValue, schedule_id: this.scheduleIdValue })
  }

  // Offering and withdrawing trades is housekeeping, not an edit: kept out of Undo.
  _withoutUndo(project, fn) {
    const stm = project.stm
    if (!stm || stm.disabled) return fn()
    if (stm.isRecording) stm.stopTransaction()
    stm.disable()
    try { return fn() } finally { stm.enable() }
  }

  _addTradeBox(field) {
    const bar = field.picker?.bbar
    if (!bar || field._tradeBox) return
    bar.insert({ type: "button", text: this.i18nAddValue, icon: "b-fa b-fa-plus", listeners: { click: () => this._addTrade(field) } }, 0)
    const box = bar.insert({ type: "textfield", placeholder: this.i18nAddTradeValue, maxLength: 50, flex: 1 }, 0)
    box.input?.addEventListener("keydown", (event) => {
      if (event.key !== "Enter") return
      event.preventDefault()
      event.stopPropagation()
      this._addTrade(field)
    })
    field._tradeBox = box
  }

  _addTrade(field) {
    const project = this.gantt?.project, box = field._tradeBox
    if (!project || !box) return
    const name = this._tradeName(box.value), key = name.toLowerCase()
    if (!name) { box.focus(); return }
    const store = project.resourceStore
    let resource = store.records.find((r) => this._tradeKey(r.name) === key)
    const isNew = !resource
    if (isNew) {
      [resource] = store.add({ name })
      if (this.crewTradesUrlValue && !this.crewTradesValue.some((t) => this._tradeKey(t) === key)) this._saveCrewTrade(name)
    }
    // Onto the picker's list (it doesn't follow the resource list by itself), ticked for this task.
    let row = field.store.find((a) => a.resource === resource, true)
    if (!row) [row] = field.store.add({ resource, event: field.projectEvent, units: 0 })
    field.value = [...field.valueCollection.values.filter((a) => a !== row), row]
    box.value = ""
    box.focus()
    if (isNew) {
      this._showToast(this.i18nTradeAddedValue.replace("%{name}", this._escapeHtml(name)))
      track("Schedule Trade Added", { crew_schedule: !!this.crewTradesUrlValue, project_id: this.projectIdValue, schedule_id: this.scheduleIdValue })
    }
  }

  async _saveCrewTrade(name) {
    this.crewTradesValue = [...this.crewTradesValue, name]
    try {
      const response = await fetch(this.crewTradesUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfTokenValue },
        body: JSON.stringify({ trade: { name }, source: "schedule" })
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
    } catch {
      this.crewTradesValue = this.crewTradesValue.filter((t) => t !== name)
      this._showToast(this.i18nTradeNotSavedValue.replace("%{name}", this._escapeHtml(name)), "alert")
    }
  }

  _escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c])
  }

  // ─── Public actions (data-action bindings) ──────────────────────────

  async newTask() {
    const project = this.gantt.project
    const taskStore = project.taskStore
    const newTask = {
      name: this.i18nNewActivityValue,
      startDate: new Date(),
      duration: 7,
      percentDone: 0
    }
    // Suspend autoSync to avoid a double sync (manual + auto)
    project.autoSync = false
    const addedTask = taskStore.add(newTask)[0]
    try {
      const response = await project.sync()
      if (response.response?.success) {
        this.gantt.scrollTaskIntoView(addedTask)
        this.gantt.features.cellEdit.startEditing({ record: addedTask, field: "name" })
      }
    } finally {
      project.autoSync = true
    }
  }

  async toggleCriticalPath() {
    const feature = this.gantt.features.criticalPaths
    feature.disabled = !feature.disabled
    // Let the engine (re)compute task.critical, then re-render the left grid
    // rows so the renderRow hook can tint them too (Larry item 26).
    try { await this.gantt.project.commitAsync() } catch { /* rows still refresh */ }
    this.gantt.refreshRows()
    this._syncToggleButtons()
  }

  // Paint the toolbar toggles by state: brand while the feature is ON, grey
  // while it's OFF. Both used to sit brand-colored all the time, so there was
  // no way to tell whether Critical Path / Baseline was actually applied.
  _syncToggleButtons() {
    const paint = (el, on) => {
      if (!el) return
      el.classList.toggle("text-brand", on)
      el.classList.toggle("hover:text-brand-dark", on)
      el.classList.toggle("text-gray-500", !on)
      el.classList.toggle("hover:text-gray-700", !on)
    }
    const f = this.gantt?.features
    if (this.hasCriticalPathBtnTarget) {
      paint(this.criticalPathBtnTarget, !!(f?.criticalPaths && !f.criticalPaths.disabled))
    }
    if (this.hasBaselineBtnTarget) {
      paint(this.baselineBtnTarget, !!(f?.baselines && !f.baselines.disabled))
    }
  }

  // Baseline bars on/off (Larry item 25). Rows grow a little so the gray
  // planned bar fits under the actual one.
  toggleBaselines() {
    const feature = this.gantt.features.baselines
    if (!feature) return
    feature.disabled = !feature.disabled
    this.gantt.rowHeight = feature.disabled ? 27 : 38
    this._syncToggleButtons()
  }

  // Auto-show baselines when the loaded schedule has any (a progress-update
  // import), so the comparison is visible without hunting for the toggle.
  _autoShowBaselines() {
    const feature = this.gantt?.features?.baselines
    if (!feature || !feature.disabled) return
    let has = false
    this.gantt.project.taskStore.forEach(t => {
      const b = t.baselines
      const count = b?.count ?? (Array.isArray(b) ? b.length : 0)
      if (count > 0) { has = true; return false }
    })
    if (has) {
      feature.disabled = false
      this.gantt.rowHeight = 38
    }
  }

  undo() {
    if (this.gantt.project.stm.canUndo) this.gantt.project.stm.undo()
  }

  redo() {
    if (this.gantt.project.stm.canRedo) this.gantt.project.stm.redo()
  }

  zoomIn() { this.gantt.zoomIn() }
  zoomOut() { this.gantt.zoomOut() }
  zoomToFit() { this.gantt.zoomToFit() }

  fullscreen() {
    const container = this.element.querySelector("[data-gantt-target='container']")?.parentElement
    if (!container) return

    if (!document.fullscreenElement) {
      container.requestFullscreen?.()
      this.containerTarget.style.height = "100%"
      this.containerTarget.style.border = "none"
    } else {
      document.exitFullscreen?.()
      // Use dynamic height calculation matching the initial resize script
      const top = this.containerTarget.getBoundingClientRect().top
      this.containerTarget.style.height = `${window.innerHeight - top - 32}px`
      this.containerTarget.style.border = "1px solid var(--color-border-light)"
    }
  }

  scrollLeft() { this.gantt.timeAxisSubGrid.scrollable.scrollBy(-100, 0) }
  scrollRight() { this.gantt.timeAxisSubGrid.scrollable.scrollBy(100, 0) }

  editCalendar() {
    const calendarManagerStore = this.gantt.project.calendarManagerStore
    const calendar = calendarManagerStore.find(cal => cal.name === "General") || calendarManagerStore.first
    if (!calendar) return

    this._ensureCalendarHasIntervals(calendar)

    const calendarEditor = new window.bryntum.gantt.CalendarEditor({
      calendar, project: this.gantt.project, centered: true, modal: true,
      listeners: {
        save: async ({ source: editor }) => {
          // The CalendarEditor already sets intervals correctly — don't touch them.
          // Just update project-level settings based on the calendar's working time mode.
          this._updateProjectDurationSettings(this.gantt.project)
          this._saveCalendarChanges(calendar)

          // Propagate so the engine recalculates dates, then explicitly sync
          // (autoSync may not detect nested calendar interval changes)
          await this.gantt.project.commitAsync()
          await this.gantt.project.propagate()
          await this.gantt.project.sync()
          this._showToast(this.i18nCalendarUpdatedValue, "notice")
        }
      }
    })
    calendarEditor.show()
  }

  exportExcel() {
    const filename = this.gantt.project.taskStore.first?.name
    this.gantt.features.excelExporter.export({ filename })
  }

  exportMsProject() {
    const filename = this.gantt.project.taskStore.first && `${this.gantt.project.taskStore.first.name}.xml`
    this.gantt.features.mspExport.export({ filename })
  }

  exportIcal() {
    if (this.gantt.selectedRecords.length === 0) {
      this._showToast(this.i18nSelectTaskFirstValue, "alert")
      return
    }
    this.gantt.selectedRecords.forEach(task => task.exportToICS())
  }

  async exportPdf() {
    const taskStore = this.gantt.project.taskStore
    let minDate = null, maxDate = null
    taskStore.forEach(task => {
      if (task.startDate && (!minDate || task.startDate < minDate)) minDate = new Date(task.startDate)
      if (task.endDate && (!maxDate || task.endDate > maxDate)) maxDate = new Date(task.endDate)
    })
    if (minDate && maxDate) {
      minDate.setDate(minDate.getDate() - 7)
      maxDate.setDate(maxDate.getDate() + 7)
      this.gantt.setTimeSpan(minDate, maxDate)
    }
    this.gantt.features.pdfExport.showExportDialog({
      scheduleRange: "daterange", rangeStart: minDate, rangeEnd: maxDate,
      orientation: "landscape", paperFormat: "A4",
      exporterType: "multipagevertical", keepRegionSizes: false
    })
    // Bryntum's list shows bare keys ("A3"); ours adds the measurements.
    this.gantt.features.pdfExport.exportDialog?.widgetMap?.paperFormatField?.store?.forEach?.((rec) => {
      const label = this._paperLabel(rec.id)
      if (label && rec.text !== label) rec.text = label
    })
  }

  _paperLabel(key) {
    const size = PAPER_SIZES[key]
    return size ? `${key} (${size})` : null
  }

  // Ledger is the 11×17 sheet turned sideways (Larry's name for it). Our PDF server
  // reads "Ledger" as already sideways and would turn it upright under Landscape,
  // so Ledger always goes out as Tabloid in landscape: 17 wide by 11 tall.
  _ledgerAsTabloid(config) {
    if (config?.paperFormat !== "Ledger") return
    config.paperFormat = "Tabloid"
    config.orientation = "landscape"
  }

  // 11×17 paper (K2-2, Larry 2026-09-24: one sheet for the meeting table). Bryntum
  // only lists the A sizes, Letter and Legal. "Tabloid" is the name our PDF server
  // prints at 11×17 in (checked: 792×1224 pt). The heights sit a hair under the
  // sheet, as Bryntum does for Letter, so a page never spills onto a blank one.
  // Ledger is listed too, the same sheet sideways (see _ledgerAsTabloid).
  _addTabloid() {
    const paper = window.bryntum?.gantt?.PaperFormat
    if (!paper) return
    if (!paper.Tabloid) paper.Tabloid = { width: 11, height: 17, portraitWidth: 11, portraitHeight: 16.95, landscapeWidth: 17, landscapeHeight: 10.8 }
    if (!paper.Ledger) paper.Ledger = { ...paper.Tabloid }
  }

  // Every PDF page starts with the project's name and number, its address, the
  // print date and the page count (K2-2). Inline styles: the PDF server only
  // gets this HTML.
  _pdfHeader({ currentPage, totalPages }) {
    const esc = (v) => String(v ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]))
    let date
    try { date = new Date().toLocaleDateString(document.documentElement.lang || undefined, { year: "numeric", month: "short", day: "numeric" }) }
    catch (_) { date = new Date().toLocaleDateString() }
    const printed = this.i18nPdfPrintedValue.replace("%{date}", date)
    // Bryntum measures the header once with -1 for the page numbers; the line keeps its height.
    const page = currentPage >= 0 && totalPages > 0 ? this.i18nPdfPageValue.replace("%{page}", currentPage + 1).replace("%{total}", totalPages) : "&nbsp;"
    const number = this.projectNumberValue ? ` <span style="font-weight:400;color:#4b5563">· ${esc(this.projectNumberValue)}</span>` : ""
    return `<div style="display:flex;justify-content:space-between;align-items:flex-end;gap:24px;padding:10px 14px 8px;border-bottom:1px solid #d1d5db;font-family:Inter,Arial,sans-serif;color:#111827;background:#fff">
        <div style="min-width:0"><div style="font-size:16px;font-weight:700;line-height:1.3">${esc(this.projectNameValue)}${number}</div>
          <div style="font-size:13px;color:#4b5563;line-height:1.4">${esc(this.projectAddressValue) || "&nbsp;"}</div></div>
        <div style="text-align:right;font-size:13px;color:#4b5563;line-height:1.4;white-space:nowrap"><div>${esc(printed)}</div><div>${page === "&nbsp;" ? page : esc(page)}</div></div>
      </div>`
  }

  changeSchedule(event) {
    const scheduleId = event.target.value
    const url = `/projects/${this.projectIdValue}/schedules/${scheduleId}`
    this._go(url)
  }

  // A page change inside the app (Turbo), never a full reload, so a screen
  // recording in the feedback panel keeps going (Keane, K3-8, 2026-10-05).
  _go(url, action = "advance") {
    if (window.Turbo) window.Turbo.visit(url, { action })
    else window.location.href = url
  }

  // ── Import modal (Larry item 25): baseline vs progress update ──

  openImportModal() {
    if (this.hasImportModalTarget) this.importModalTarget.classList.remove("hidden")
  }

  hideImportModal() {
    if (this.hasImportModalTarget) this.importModalTarget.classList.add("hidden")
  }

  chooseImportFile() {
    const checked = this.hasImportModalTarget &&
      this.importModalTarget.querySelector("input[name='import_mode']:checked")
    this._importMode = checked?.value || "baseline"
    this.hideImportModal()
    if (this.hasImportFileTarget) this.importFileTarget.click()
  }

  importFile() {
    if (!this.hasImportFileTarget) return
    const file = this.importFileTarget.files[0]
    if (!file) return

    // .xml = Microsoft Project's XML export (Keane, K3-8, 2026-10-05): the
    // server's project reader takes it the same way as an .mpp.
    const ext = file.name.split(".").pop().toLowerCase()
    if (!["xer", "mpp", "xml"].includes(ext)) {
      this._showToast(this.i18nOnlyMppXerValue, "alert")
      return
    }

    // Import mode (item 25): "baseline" stamps the imported dates as the
    // baseline; "update" replaces the schedule but carries the CURRENT
    // baselines over to the new rows, matched by phase and activity name.
    const mode = this._importMode || "baseline"
    this._importMode = null
    const savedBaselines = mode === "update" ? collectBaselines(this.gantt.project.taskStore) : null

    this.gantt.maskBody(this.i18nImportingValue)
    const formData = new FormData()
    formData.append("mpp_file", file)

    fetch(this.importUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfTokenValue },
      body: formData
    })
    .then(r => r.json())
    .then(async response => {
      if (response.success && response.data) {
        this.gantt.project.autoSync = false
        this.gantt.project.clear()

        const importer = new BryntumImporter({
          gantt: this.gantt,
          syncUrl: this.syncUrlValue,
          csrfToken: this.csrfTokenValue,
          // Dates are read as printed for BOTH modes. A progress update is a
          // snapshot of where the job actually is (Larry 2026-08-04 — the
          // engine dragged every start back to the project start date), and
          // a baseline is by definition "the plan as printed": letting the
          // engine reschedule first was why every imported baseline landed
          // stacked on a single day (Larry 2026-08-06, "all on March 2nd" —
          // the baseline stamps whatever dates the tasks hold).
          preserveDates: true
        })

        const project = await importer.importData(response.data, this.scheduleIdValue)
        await this._applyImportBaselines(project, mode, savedBaselines)
        this.gantt.unmaskBody()

        // Reload to get a clean render with proper zoom, calendar, and time axis.
        // Data is already synced to the backend by the importer (_go: not a
        // full reload, so a screen recording keeps going).
        sessionStorage.setItem("gantt_flash", JSON.stringify({ message: this.i18nImportSuccessValue, type: "notice" }))
        this._go(window.location.href, "replace")
      } else {
        this.gantt.unmaskBody()
        this._showToast(response.error || this.i18nImportFailedValue, "alert")
      }
    })
    .catch(error => {
      this.gantt.unmaskBody()
      this._showToast(`Import error: ${error.message}`, "alert")
    })

    // Reset file input
    this.importFileTarget.value = ""
  }

  // Stamp baselines onto the freshly imported tasks, then sync the extra
  // field. Baseline mode: every dated leaf gets its own imported dates.
  // Update mode: the pre-import baselines land on the matching activities
  // (same phase and name; renamed/new activities simply have no baseline).
  async _applyImportBaselines(project, mode, savedBaselines) {
    if (!project?.taskStore) return
    let touched = 0
    project.taskStore.forEach(t => {
      if (!t.isLeaf) return
      if (mode === "baseline") {
        if (t.startDate && t.endDate) {
          t.baselines = [{ startDate: t.startDate, endDate: t.endDate }]
          touched++
        }
      } else if (savedBaselines) {
        const match = baselineFor(savedBaselines, t)
        if (match) {
          t.baselines = [{ startDate: match.startDate, endDate: match.endDate }]
          touched++
        }
      }
    })
    if (touched > 0) {
      try { await project.sync() } catch (e) { console.warn("Baseline sync failed:", e) }
    }
  }

  editStartedAt() {
    if (this.hasStartedAtModalTarget) {
      this.startedAtModalTarget.classList.remove("hidden")
    }
  }

  hideStartedAt() {
    if (this.hasStartedAtModalTarget) {
      this.startedAtModalTarget.classList.add("hidden")
    }
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  showDeleteModal() {
    if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.remove("hidden")
  }

  hideDeleteModal() {
    if (this.hasDeleteModalTarget) this.deleteModalTarget.classList.add("hidden")
  }

  async deleteSchedule() {
    try {
      const response = await fetch(this.deleteUrlValue, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": this.csrfTokenValue,
          "Accept": "application/json"
        }
      })
      const data = await response.json()
      if (data.success) {
        this._go(data.redirect_url)
      } else {
        this._showToast(data.error || "Failed to delete schedule", "alert")
        this.hideDeleteModal()
      }
    } catch {
      this._showToast("Failed to delete schedule", "alert")
      this.hideDeleteModal()
    }
  }

  saveStartedAt() {
    if (!this.hasStartedAtInputTarget) return
    const dateVal = this.startedAtInputTarget.value
    if (!dateVal) return

    fetch(`/projects/${this.projectIdValue}/schedules/update_started_at`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfTokenValue
      },
      body: JSON.stringify({ start_at: dateVal })
    }).then(() => {
      if (this.hasStartDateDisplayTarget) {
        const d = new Date(dateVal + "T00:00:00")
        const localeMap = { br: "pt-BR", es: "es", jp: "ja", de: "de", it: "it", fr: "fr" }
        const dateLocale = localeMap[this.localeValue] || "en-US"
        this.startDateDisplayTarget.textContent = d.toLocaleDateString(dateLocale, { year: "numeric", month: "long", day: "numeric" })
      }
      this.gantt.project.startDate = new Date(dateVal)
      this.hideStartedAt()
      this._showToast(this.i18nStartDateUpdatedValue, "notice")
    }).catch((err) => {
      console.error("Failed to update start date:", err)
      this._showToast(this.i18nStartDateFailedValue, "alert")
    })
  }

  // ─── Private initialization ─────────────────────────────────────────

  async _initLocale() {
    const { LocaleHelper, LocaleManager } = window.bryntum.gantt

    // Await the locale import so labels are correct before Gantt renders
    if (this.localeValue === "br") {
      const mod = await import("../lib/bryntum_locale_pt_br")
      LocaleHelper.publishLocale(mod.default)
      LocaleManager.locale = "PtBr"
    } else if (this.localeValue === "es") {
      const mod = await import("../lib/bryntum_locale_es")
      LocaleHelper.publishLocale(mod.default)
      LocaleManager.locale = "Es"
    }
  }

  _initProject() {
    const { ProjectModel, StateTrackingManager, HasPercentDoneMixin } = window.bryntum.gantt

    class MyProjectModel extends HasPercentDoneMixin.derive(ProjectModel) {}

    this.project = new MyProjectModel({
      taskStore: {
        transformFlatData: true,
        wbsMode: "auto",  // Auto-recalculate WBS codes on reorder/restructure
        // MS Project "Quantity" custom field, populated at import (see BryntumImporter).
        fields: [{ name: "quantity", type: "string" }]
      },
      transport: {
        load: {
          url: this.loadUrlValue,
          requestConfig: { headers: { "X-CSRF-Token": this.csrfTokenValue } }
        },
        sync: {
          url: this.syncUrlValue,
          requestConfig: { headers: { "X-CSRF-Token": this.csrfTokenValue } }
        }
      },
      autoLoad: true,
      autoSync: true,
      autoSetConstraints: true,  // Auto-add SNET constraint when user drags a task
      resetUndoRedoQueuesAfterLoad: true,  // Clear undo queue after data load
      stm: new StateTrackingManager({ autoRecord: true }),
      startDate: this.startDateValue || undefined,
      hoursPerDay: 8,
      daysPerWeek: 5,
      daysPerMonth: 20,
      // Use the task's working-time calendar when scheduling manually scheduled
      // tasks — not Bryntum's 24/7 default. Keeps MS Project / MPP imports
      // (always manuallyScheduled=true) consistent with the source.
      skipNonWorkingTimeWhenSchedulingManually: true,
      skipNonWorkingTimeInDurationWhenSchedulingManually: true,
      // Silently resolve scheduling issues during load. After load completes,
      // these listeners are replaced with Bryntum's built-in popup dialogs
      // so the user sees resolution options for conflicts during manual edits.
      listeners: {
        emptycalendar({ schedulingIssue }) {
          const calName = schedulingIssue?.getCalendar?.()?.name || "unknown"
          console.warn(`Auto-resolving empty calendar "${calName}" — applying 8-hour work day`)
          const resolutions = schedulingIssue?.getResolutions?.() || []
          // Resolution[1] = 8-hour calendar (08:00-12:00, 13:00-17:00)
          const use8hrIndex = resolutions.length > 1 ? 1 : 0
          resolutions[use8hrIndex]?.resolve?.()
        },
        schedulingconflict({ continueWithResolutionResult, conflict }) {
          console.warn("Auto-resolving scheduling conflict:", conflict?.description)
          const resolutions = conflict?.getResolutions?.() || []
          // Pick the last resolution (typically "remove constraint")
          continueWithResolutionResult(Math.max(resolutions.length - 1, 0))
        },
        cycle: (event) => this._settleLoadCycle(event)
      }
    })
    // No default calendar created here — calendars are loaded from the DB.
    // The load handler in _attachListeners() establishes the project calendar.
  }

  _initGantt() {
    const { Gantt } = window.bryntum.gantt
    const settings = this.ganttSettingsValue || {}

    let columns = this._buildColumns(settings)

    // Center the initial viewport on today so there's no visible scroll jump
    const today = new Date()
    const viewStart = new Date(today)
    viewStart.setMonth(viewStart.getMonth() - 1)
    const viewEnd = new Date(today)
    viewEnd.setMonth(viewEnd.getMonth() + 5)

    this.gantt = new Gantt({
      appendTo: this.containerTarget,
      readOnly: this.isReadOnlyValue,
      // Bryntum's issue pop-ups wait for a click; during the load nobody is
      // there to click, and a waiting load keeps saving paused (Douglas
      // Stapley, 2026-10-27). The load handler turns them on once it's done.
      displaySchedulingIssueResolutionPopup: false,
      dependencyIdField: "wbsCode",
      startDate: viewStart,
      endDate: viewEnd,
      infiniteScroll: true,
      selectionMode: { cell: true, dragSelect: true, rowNumber: true },
      project: this.project,
      features: {
        // Critical path (starts disabled, toggled by toolbar button)
        criticalPaths: { disabled: true },

        // Baseline bars (Larry item 25) — gray planned bar under the actual.
        // Starts hidden; auto-enables on load when the data has baselines.
        baselines: { disabled: true },

        // Dependencies — wider click target for easier selection
        dependencies: { radius: 3, clickWidth: 5 },
        dependencyEdit: true,

        // Cell editing
        cellEdit: true,

        // Task editor dialog
        taskEdit: {
          editorConfig: {
            width: "48em",
            modal: { closeOnMaskTap: true }
          }
        },

        // Row reordering
        rowReorder: { showGrip: true },

        // Timeline features
        // The current-time line reads "Today", not the clock time — the
        // default currentDateFormat ('HH:mm') showed "16:14", which looks
        // like data on a schedule measured in days (Phelipe 2026-08-06).
        timeRanges: {
          showCurrentTimeLine: { name: this.i18nTodayValue },
          showHeaderElements: true
        },

        // Bryntum's "Project start"/"Project end" lines sit at the first and
        // last task dates, which reads as the contract deadline but isn't one.
        // The Original/Revised Completion markers from tasks#load replace them.
        projectLines: { disabled: true },
        nonWorkingTime: true,

        // Column filtering
        filter: true,

        // Export features
        excelExporter: { dateFormat: "YYYY-MM-DD HH:mm" },
        mspExport: true,
        pdfExport: {
          exportServer: "https://portal.conslog.com/pdf-server/",
          translateURLsToAbsolute: window.location.origin,
          sendAsBinary: true, paperFormat: "A4", orientation: "landscape",
          lockPaperSize: true, exporterType: "multipagevertical",
          scheduleRange: "daterange", rowsRange: "all",
          keepRegionSizes: false, showDefaultExportDialog: true,
          headerTpl: (page) => this._pdfHeader(page),
          exportDialog: {
            items: { paperFormatField: { hidden: false }, orientationField: { hidden: false } }
          }
        },

        // Context menu
        taskMenu: {
          items: {
            exportToIcs: {
              icon: "b-fa fa-calendar-alt", text: this.i18nAddToCalendarValue, weight: 900,
              onItem({ taskRecord }) { taskRecord.exportToICS() }
            }
          }
        }
      },
      columns,
      rowHeight: 27,
      barMargin: 4,
      listeners: {
        // Critical-path rows (Larry item 26): tint the left task-list row of
        // every critical task while the toggle is on (CSS keys on .crit-row,
        // scoped to the locked subgrid).
        renderRow: ({ row, record }) => {
          const g = this.gantt
          const crit = !!g && !g.features.criticalPaths.disabled && record.critical === true
          if (crit) row.addCls("crit-row")
          else row.removeCls("crit-row")
        },
        beforeCellEditStart: ({ editorContext }) => {
          const { record } = editorContext || {}
          if (!record) return
          record._originalStartDate = record.startDate ? new Date(record.startDate) : null
          record._originalEndDate = record.endDate ? new Date(record.endDate) : null
        },
        splitterDragEnd: () => this._debouncedSaveGanttSettings(),
        beforePdfExport: ({ config }) => this._ledgerAsTabloid(config),
        finishCellEdit: async ({ grid, editorContext }) => {
          try {
            const { column, record } = editorContext || {}
            if (!record || !column) return
            if (column.type === "startdate") {
              // When editing start date, keep the original end date (adjust duration)
              const originalEnd = record._originalEndDate || record.endDate
              await record.setStartDate(record.startDate, true)
              if (originalEnd) await record.setEndDate(originalEnd, false)
              if (grid?.project) await grid.project.commitAsync?.()
            } else if (column.type === "enddate") {
              await record.setEndDate(record.endDate, false)
              if (grid?.project) await grid.project.commitAsync?.()
            }
            delete record._originalStartDate
            delete record._originalEndDate
          } catch (e) { console.error("Error adjusting duration on cell edit", e) }
        }
      }
    })

    if (settings.leftSectionWidth) {
      this.gantt.subGrids.locked.width = settings.leftSectionWidth
    }

    // Crew Schedule trades in the Resources picker (K2-3). Low priority: the
    // column fills its picker first, then the trades join it.
    this.gantt.on({
      beforeCellEditStart: (event) => this._offerTrades(event),
      finishCellEdit: () => this._settleTrades(),
      cancelCellEdit: () => this._settleTrades(),
      prio: -100
    })

    // Column resize/change listeners (debounced to avoid excessive requests)
    this.gantt.on("columnresize", () => this._debouncedSaveGanttSettings())
    this.gantt.columns.on("columnhide", () => this._debouncedSaveGanttSettings())
    this.gantt.columns.on("columnshow", () => this._debouncedSaveGanttSettings())
    this.gantt.columns.on("change", event => { if (event.action === "add") this._debouncedSaveGanttSettings() })
  }

  _attachListeners() {
    const project = this.project
    const gantt = this.gantt

    project.on("load", () => {
      try {
        // Suspend autoSync while applying cosmetic load-time fixes.
        project.autoSync = false

        // Establish project calendar from DB data (no client-side default exists)
        let calendar = project.calendarManagerStore.find(cal => cal.name === "General")
          || project.calendarManagerStore.first

        if (calendar) {
          this._ensureCalendarHasIntervals(calendar)
          project.calendar = calendar
        } else {
          // No calendars in DB — create a default (will sync to DB on next change)
          const { CalendarModel } = window.bryntum.gantt
          calendar = new CalendarModel({
            name: "General",
            unspecifiedTimeIsWorking: false,
            intervals: this._getDefaultIntervals()
          })
          project.calendarManagerStore.add(calendar)
          project.calendar = calendar
        }

        // Assign calendar to tasks that don't have one
        if (project.taskStore?.forEach) {
          project.taskStore.forEach(task => { if (!task.calendar) task.calendar = calendar })
        }

        this._updateProjectDurationSettings(project)
        project.stm.enable()

        // Clear pending changes from load-time fixes before resuming autoSync.
        for (const store of [project.taskStore, project.calendarManagerStore,
          project.dependencyStore, project.resourceStore, project.assignmentStore]) {
          store.commit?.()
        }
        project.autoSync = true

        // Switch from silent auto-resolution to popup-based resolution for user edits
        project.un("emptycalendar")
        project.un("schedulingconflict")
        gantt.bindSchedulingIssueResolutionToProject(project)

        // Baseline comparison (item 25): show the gray planned bars whenever
        // the loaded schedule carries baseline data.
        this._autoShowBaselines()
        // Reflect the resulting on/off state in the toolbar (baselines may
        // have just auto-enabled above).
        this._syncToggleButtons()
      } catch (error) {
        console.error("Error in project load handler:", error)
        if (project?.stm) project.stm.enable()
        project.autoSync = true
      } finally {
        this._loadDone = true
        this._saveWatchArmed = true
        this._tellLoopLinks()
      }
    })

    // The saving safety net watches from the start; it acts only once the
    // load is done or the person has touched the schedule.
    this._startSaveWatch()

    // Notify user on sync failures so changes aren't silently lost
    project.on("syncfail", ({ response }) => {
      console.error("Sync failed:", response)
      const message = response?.parsedJson?.message || this.i18nSyncFailedValue
      this._showToast(message, "alert")
      project.autoSync = true
    })
  }

  // ─── Loops on load (Douglas Stapley, Harman Huffman, 2026-10-27) ──────
  // Activities linked in a loop (A → B → A, or a summary linked to its own
  // activity) stop Bryntum's first calculation until someone picks a fix in a
  // pop-up, and saving stays paused meanwhile. While loading, switch one link
  // of the loop off instead (Bryntum's own "Deactivate dependency" fix). It
  // is not saved — the load handler clears load-time changes — so the link
  // stays as imported and the notice shows on each open until it is fixed.
  // After the load, Bryntum's pop-up asks the person as before.
  _settleLoadCycle({ schedulingIssue, continueWithResolutionResult }) {
    if (this._loadDone) return
    const resume = window.bryntum?.gantt?.EffectResolutionResult?.Resume || "Resume"
    const cancel = window.bryntum?.gantt?.EffectResolutionResult?.Cancel || "Cancel"
    this._loopLinks = this._loopLinks || []
    try {
      const invalid = schedulingIssue?.getInvalidDependencies?.() || []
      const loop = invalid.length ? invalid : (schedulingIssue?.getDependencies?.() || [])
      const link = loop.find((dep) => dep.active !== false) || loop[0]
      if (!link || this._loopLinks.length >= 50) return continueWithResolutionResult(cancel)
      link.active = false
      this._loopLinks.push(`${link.fromEvent?.name || "?"} → ${link.toEvent?.name || "?"}`)
      console.warn("Loop on load: switched off the link", this._loopLinks.at(-1))
      continueWithResolutionResult(resume)
    } catch (error) {
      console.error("Couldn't settle a loop on load:", error)
      continueWithResolutionResult(cancel)
    }
  }

  _tellLoopLinks() {
    const links = this._loopLinks || []
    if (!links.length) return
    this._showToast(this.i18nLoopLinkOffValue.replace("%{links}", links.slice(0, 3).join(", ")), "alert")
    track("Schedule Loop Links Ignored", { project_id: this.projectIdValue, schedule_id: this.scheduleIdValue, count: links.length })
  }

  // ─── Saving safety net (Douglas Stapley, Harman Huffman, 2026-10-27) ───
  // Bryntum pauses saving while it works (the load's first calculation, a
  // cell edit, the task pop-up, a drag) and lifts the pause when that step
  // ends. If the step fails part-way the pause never lifts, and every later
  // change sits unsaved without a word (schedule 10192: autoSyncSuspendCounter
  // stuck at 1 after an MS Project XML progress update). Every 3 s: saving
  // paused, changes waiting, nothing being edited, dragged or calculated, two
  // checks in a row → lift the pause so the changes save.
  SAVE_WATCH_MS = 3000

  _startSaveWatch() {
    this._stopSaveWatch()
    this._stuckChecks = 0
    this._saveWatch = setInterval(() => this._checkSaveWatch(), this.SAVE_WATCH_MS)
    // A held mouse button = a drag or resize in progress; touching the
    // schedule also arms the watch when the load never finished.
    this._onPointerDown = () => { this._pointerDown = true; this._saveWatchArmed = true }
    this._onPointerUp = () => { this._pointerDown = false }
    this._onKeyDown = () => { this._saveWatchArmed = true }
    this.containerTarget.addEventListener("pointerdown", this._onPointerDown, true)
    this.containerTarget.addEventListener("keydown", this._onKeyDown, true)
    window.addEventListener("pointerup", this._onPointerUp, true)
    window.addEventListener("pointercancel", this._onPointerUp, true)
  }

  _stopSaveWatch() {
    clearInterval(this._saveWatch)
    this._saveWatch = null
    if (this._onPointerDown) {
      this.containerTarget?.removeEventListener("pointerdown", this._onPointerDown, true)
      this.containerTarget?.removeEventListener("keydown", this._onKeyDown, true)
      window.removeEventListener("pointerup", this._onPointerUp, true)
      window.removeEventListener("pointercancel", this._onPointerUp, true)
      this._onPointerDown = null
    }
  }

  _checkSaveWatch() {
    const project = this.gantt?.project
    if (!project || project.isDestroyed || !this._saveWatchArmed) return
    const stuck = project.isAutoSyncSuspended && project.autoSync &&
      project.crudStoreHasChanges?.() && !this._scheduleBusy(project)
    if (!stuck) { this._stuckChecks = 0; return }
    if (++this._stuckChecks < 2) return
    this._stuckChecks = 0
    const paused = project.autoSyncSuspendCounter
    while (project.isAutoSyncSuspended) project.resumeAutoSync(true)
    console.warn(`[gantt] Saving was paused (${paused}) with changes waiting; resumed it`)
    track("Schedule Autosave Resumed", { project_id: this.projectIdValue, schedule_id: this.scheduleIdValue, paused_count: paused, after_load: !!this._loadDone })
  }

  _scheduleBusy(project) {
    const gantt = this.gantt, features = gantt?.features || {}
    const pending = typeof project.hasPendingAutoCommit === "function" ? project.hasPendingAutoCommit() : project.hasPendingAutoCommit
    return Boolean(this._pointerDown || this._tradeOffer || gantt?.isResolving ||
      features.cellEdit?.isEditing || features.taskEdit?.isEditing ||
      project.isLoadingOrSyncing || pending || project.isPerformingCommit || project.graph?.isCommitting)
  }

  // ─── Calendar helpers ───────────────────────────────────────────────

  _getDefaultIntervals() {
    return [
      { recurrentStartDate: "on Sat at 0:00", recurrentEndDate: "on Mon at 0:00", isWorking: false },
      { recurrentStartDate: "on Mon,Tue,Wed,Thu,Fri at 8:00", recurrentEndDate: "on Mon,Tue,Wed,Thu,Fri at 12:00", isWorking: true },
      { recurrentStartDate: "on Mon,Tue,Wed,Thu,Fri at 12:00", recurrentEndDate: "on Mon,Tue,Wed,Thu,Fri at 13:00", isWorking: false },
      { recurrentStartDate: "on Mon,Tue,Wed,Thu,Fri at 13:00", recurrentEndDate: "on Mon,Tue,Wed,Thu,Fri at 17:00", isWorking: true },
      { recurrentStartDate: "on Mon,Tue,Wed,Thu,Fri at 17:00", recurrentEndDate: "on Mon,Tue,Wed,Thu,Fri at 8:00", isWorking: false }
    ]
  }

  _ensureCalendarHasIntervals(calendar) {
    const hasSpecific = calendar.intervals?.some(i =>
      i.isWorking && i.recurrentStartDate?.includes(" at ") && i.recurrentEndDate?.includes(" at ")
    )
    if (!hasSpecific && calendar.unspecifiedTimeIsWorking === false) {
      calendar.intervals = this._getDefaultIntervals()
    }
  }

  _updateProjectDurationSettings(project) {
    const cal = project.calendar
    if (cal?.unspecifiedTimeIsWorking === true) {
      project.hoursPerDay = 24; project.daysPerWeek = 7; project.daysPerMonth = 30
      return
    }

    // Derive hours/day and days/week from the project calendar instead of
    // assuming an 8-hour day. MS Project / MPP schedules commonly use a longer
    // construction workday (this surfaced with a 10h, 8am–6pm calendar): when we
    // forced hoursPerDay=8 onto a 10h calendar, every "N-day" task was computed
    // as N×8h and finished mid-day, so each FS successor started early and the
    // error cascaded down dependency chains (tasks off by 1+ days, summary
    // durations going fractional like 141.8d). The file's own hoursPerDay is
    // honored at import time; this keeps it honored on every reload too.
    const { hoursPerDay, daysPerWeek } = this._deriveWorkingTime(cal)
    project.hoursPerDay = hoursPerDay
    project.daysPerWeek = daysPerWeek
    project.daysPerMonth = daysPerWeek * 4
  }

  // Measure the calendar's actual working time so duration↔date math matches the
  // source schedule. Tries the engine's own working-time accumulation first
  // (handles lunch breaks / partial days / any interval format); if that isn't
  // usable yet (calendar not committed) it parses the recurrent interval strings
  // directly. Falls back to the historical 8h/5d only if both fail.
  _deriveWorkingTime(calendar) {
    const fallback = { hoursPerDay: 8, daysPerWeek: 5 }
    if (!calendar) return fallback

    if (typeof calendar.calculateDurationMs === "function") {
      try {
        // Sample one DST-free week (Jan 1 2024 is a Monday) and ask the calendar
        // how much working time each day holds.
        let workingDays = 0
        let totalHours = 0
        for (let i = 0; i < 7; i++) {
          const dayStart = new Date(2024, 0, 1 + i, 0, 0, 0, 0)
          const dayEnd = new Date(2024, 0, 2 + i, 0, 0, 0, 0)
          const hours = calendar.calculateDurationMs(dayStart, dayEnd) / 3600000
          if (hours > 0) { workingDays++; totalHours += hours }
        }
        if (workingDays > 0 && totalHours > 0) {
          // Round to 2 decimals to absorb float noise (e.g. 9.999999 → 10).
          return { hoursPerDay: Math.round((totalHours / workingDays) * 100) / 100, daysPerWeek: workingDays }
        }
      } catch (e) {
        console.warn("[gantt] Engine working-time calc failed; parsing intervals instead", e)
      }
    }

    return this._workingTimeFromIntervals(calendar) || fallback
  }

  // Timing-independent fallback: parse the calendar's recurrent working intervals
  // (e.g. "on MONDAY,TUESDAY,...,FRIDAY at 08:00" → "...at 18:00") into hours per
  // working weekday. Handles full or abbreviated day names and multiple working
  // blocks per day (lunch breaks). Returns null if it can't parse confidently.
  _workingTimeFromIntervals(calendar) {
    const raw = calendar?.intervals
    let intervals = null
    if (Array.isArray(raw)) intervals = raw
    else if (raw && typeof raw.map === "function") intervals = raw.map(i => i.data || i)
    else if (raw?.allRecords) intervals = raw.allRecords.map(i => i.data)
    if (!intervals || !intervals.length) return null

    const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]
    const parse = (s) => {
      const m = /^on\s+(.+?)\s+at\s+(\d{1,2}):(\d{2})/i.exec(s || "")
      if (!m) return null
      const days = m[1].split(",").map(d => DAYS.indexOf(d.trim().slice(0, 3).toLowerCase())).filter(i => i >= 0)
      return { days, minutes: parseInt(m[2], 10) * 60 + parseInt(m[3], 10) }
    }

    const hoursByDay = [0, 0, 0, 0, 0, 0, 0]
    for (const iv of intervals) {
      if (!iv?.isWorking) continue
      const a = parse(iv.recurrentStartDate), b = parse(iv.recurrentEndDate)
      if (!a || !b || !a.days.length) return null
      let mins = b.minutes - a.minutes
      if (mins <= 0) mins += 24 * 60
      for (const d of a.days) hoursByDay[d] += mins / 60
    }

    const workingDays = hoursByDay.filter(h => h > 0).length
    const total = hoursByDay.reduce((s, h) => s + h, 0)
    if (!workingDays || !total) return null
    return { hoursPerDay: Math.round((total / workingDays) * 100) / 100, daysPerWeek: workingDays }
  }

  _saveCalendarChanges(calendar) {
    if (this.gantt.project.calendar !== calendar) this.gantt.project.calendar = calendar
    const store = this.gantt.project.calendarManagerStore
    if (!store.includes(calendar)) store.add(calendar)
  }

  // ─── Column building ────────────────────────────────────────────────

  _buildColumns(settings) {
    const defaultCols = [
      { type: "wbs", hidden: true },
      { type: "name", width: 250, text: this.i18nActivityValue, showWbs: true },
      { type: "startdate", width: 50 },
      { type: "enddate", width: 50 },
      { type: "duration", width: 50, renderer: this._durationColumnRenderer },
      { type: "resourceassignment", width: 100, text: this.i18nResourcesValue },
      { field: "quantity", text: this.i18nQuantityValue, width: 120 },
      { text: this.i18nPctDoneValue, field: "percentDone", type: "percentdone", mode: "circle", showCircle: true, width: 50, sortable: false, group: false, editor: true }
    ]

    if (!settings.columns) return defaultCols

    try {
      const cols = [
        { type: "wbs", hidden: settings.columns.find(c => c.type === "wbs")?.hidden ?? true, width: settings.columns.find(c => c.type === "wbs")?.width ?? 70 },
        { type: "name", hidden: settings.columns.find(c => c.type === "name")?.hidden ?? false, width: settings.columns.find(c => c.type === "name")?.width ?? 250, text: this.i18nActivityValue, showWbs: true },
        { type: "startdate", hidden: settings.columns.find(c => c.type === "startdate")?.hidden ?? false, width: settings.columns.find(c => c.type === "startdate")?.width ?? 50 },
        { type: "enddate", hidden: settings.columns.find(c => c.type === "enddate")?.hidden ?? false, width: settings.columns.find(c => c.type === "enddate")?.width ?? 50 },
        { type: "duration", hidden: settings.columns.find(c => c.type === "duration")?.hidden ?? false, width: settings.columns.find(c => c.type === "duration")?.width ?? 50, renderer: this._durationColumnRenderer },
        { type: "resourceassignment", hidden: settings.columns.find(c => c.type === "resourceassignment")?.hidden ?? false, width: settings.columns.find(c => c.type === "resourceassignment")?.width ?? 100, text: this.i18nResourcesValue },
        { field: "quantity", text: this.i18nQuantityValue, hidden: settings.columns.find(c => c.name === "quantity")?.hidden ?? false, width: settings.columns.find(c => c.name === "quantity")?.width ?? 120 },
        { text: this.i18nPctDoneValue, field: "percentDone", type: "percentdone", hidden: settings.columns.find(c => c.type === "percentdone")?.hidden ?? false, width: settings.columns.find(c => c.type === "percentdone")?.width ?? 50, mode: "circle", showCircle: true, sortable: false, group: false, editor: true }
      ]
      return cols
    } catch {
      return defaultCols
    }
  }

  // Duration cell renderer. Elapsed-duration tasks (MS Project "ed") are stored
  // as clock-hours on a 24/7 calendar so they schedule continuously across
  // nights/weekends/holidays — but raw "168 hours" reads poorly, and "7 days"
  // would be indistinguishable from a normal 7-working-day task. Show whole-day
  // elapsed values with MS Project's compact "ed" suffix ("7 ed") so they stay
  // visually distinct, with a tooltip spelling it out. Everything else (incl.
  // sub-day elapsed) falls through to Bryntum's default rendering.
  // `this` is the DurationColumn when Bryntum invokes this; we use the context's
  // `column` so binding doesn't matter.
  _durationColumnRenderer(renderData) {
    const { record, column, cellElement } = renderData
    const cal = record?.calendar
    if (cal?.unspecifiedTimeIsWorking === true &&
        /^h/i.test(record.durationUnit || "") &&
        record.duration % 24 === 0) {
      if (cellElement) cellElement.title = "Elapsed days — runs continuously, including nights, weekends and holidays"
      return `${record.duration / 24} ed`
    }
    return column.defaultRenderer(renderData)
  }

  // ─── Settings persistence ───────────────────────────────────────────

  _debouncedSaveGanttSettings() {
    clearTimeout(this._settingsSaveTimer)
    this._settingsSaveTimer = setTimeout(() => this._saveGanttSettings(), 500)
  }

  _saveGanttSettings() {
    // The theme can be picked before the gantt finishes booting; don't blow up
    // reading columns off a gantt that isn't there yet.
    if (!this.gantt) return

    const columns = this.gantt.columns.map(col => ({
      name: col.field, type: col.type, width: col.width, hidden: col.hidden
    }))
    const leftSectionWidth = this.gantt.subGrids.locked.width
    // No `theme` any more: the chart follows the app. Saving without it also
    // clears a schedule-only theme stuck from July (see _ganttTheme).
    const settings = JSON.stringify({ columns, leftSectionWidth })

    fetch(this.saveSettingsUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": this.csrfTokenValue
      },
      body: `settings=${encodeURIComponent(settings)}`
    })
  }

  // ─── Event bridge ───────────────────────────────────────────────────

  _onHealthUpdate(event) {
    const { taskId, percentDone } = event.detail || {}
    if (!taskId || !this.gantt?.project?.taskStore) return
    const task = this.gantt.project.taskStore.getById(taskId)
    if (task) {
      task.percentDone = percentDone
      // autoSync will handle persisting the change
    }
  }

  // ─── Toast ──────────────────────────────────────────────────────────

  _showToast(message, type = "notice") {
    const cssClass = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    const iconSvg = type === "alert"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
    const toast = document.createElement("div")
    toast.className = cssClass
    toast.setAttribute("data-controller", "flash")
    toast.setAttribute("data-flash-dismiss-after-value", "5000")
    toast.setAttribute("role", "alert")
    toast.innerHTML = `<div class="shrink-0">${iconSvg}</div><p class="flex-1 text-sm">${message}</p>`
    document.body.prepend(toast)
  }

  // ─── Schedule appearance ────────────────────────────────────────────
  // The chart follows the app's light/dark theme. It briefly had its own
  // choice in the ⋮ menu (added 07-23, removed 07-28), and a choice saved in
  // that week kept winning with no way left to undo it: the chart stayed
  // white with light-grey text in dark mode (Phelipe 2026-09-22). A saved
  // one is ignored now, and dropped the next time settings save.
  _ganttTheme() {
    return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light"
  }

  _syncGanttThemeStylesheets() {
    const theme = this._ganttTheme()
    const link = document.getElementById("gantt-theme")
    if (!link) return
    const expected = theme === "dark" ? "/vendor/bryntum/gantt.material-dark.css" : "/vendor/bryntum/gantt.material.css"
    if (!link.href.endsWith(expected)) link.href = expected
    // The chart's own theme, for gantt-custom.css and the page's <style>:
    // every text colour keys on this, so text always matches the chart.
    if (document.documentElement.getAttribute("data-gantt-theme") !== theme) {
      document.documentElement.setAttribute("data-gantt-theme", theme)
    }
  }

  _showPendingFlash() {
    const raw = sessionStorage.getItem("gantt_flash")
    if (!raw) return
    sessionStorage.removeItem("gantt_flash")
    try {
      const { message, type } = JSON.parse(raw)
      this._showToast(message, type)
    } catch { /* ignore */ }
  }
}
